import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { useLatest } from '../useLatest'
import { fetchStaatidMerker, UNKNOWN_ERROR } from './api'
import type { StaatidParams, StaatidMerkerResponse, StaatidGruppering } from './api'
import MerkerTable from './MerkerTable'
import MerkeDetalj from './MerkeDetalj'
import StaatidFilterBar, { EMPTY_LOCAL_FILTERS } from './StaatidFilterBar'
import type { StaatidLocalFilters } from './StaatidFilterBar'
import FilterSlot, { useLocalFilter } from '../FilterSlot'

const LIMIT = 50
const DEBOUNCE_MS = 300 // as Bruktbil → Merke & modell's search (#476)

interface Props {
  /** 'modell' is the Modeller tab (#695): every model across merker, and a
   *  row opens the model page directly. Default: the Merke & modell grid. */
  gruppering?: StaatidGruppering
}

export default function MerkerTab({ gruppering = 'merke' }: Props) {
  const { t } = useTranslation('staatid')
  const perModell = gruppering === 'modell'
  const { from, to, selgerPostnummer } = useBruktbilFilters()
  // #530 AC6 — same gap the Forhandlere tab had: the antall-ledd chips never
  // reached this tab, so the merke grid and its detail chart were unfilterable.
  // One applied value feeds both, so the two screens count the same rows.
  const [localDraft, setLocalDraft, localFilters] =
    useLocalFilter<StaatidLocalFilters>('staatid', EMPTY_LOCAL_FILTERS)
  const [searchParams, setSearchParams] = useSearchParams()
  // Detail drill lives in the URL so it is deep-linkable, same as the dealer
  // side's ?forhandler=. Own param names: `merke`/`modell` are already taken
  // by the Spørring tab's filter state (#490).
  const merkeDetalj = searchParams.get('merkeDetalj')
  const modellDetalj = searchParams.get('modellDetalj')

  const [merker, setMerker] = useState<StaatidMerkerResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Server-side search (#695): the box binds to `search`, the query to the
  // debounced `q`.
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')

  const filters: StaatidParams = { from, to, selgerPostnummer, ...localFilters }
  const filterKey = `${from}|${to}|${selgerPostnummer ?? ''}|${JSON.stringify(localFilters)}`
  // A page number belongs to one result list: new filters or a new search
  // start from page 1 without a stale request for the old page first.
  const listKey = `${filterKey}|${q}`
  const [page, setPage] = useState({ key: listKey, offset: 0 })
  const offset = page.key === listKey ? page.offset : 0

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useLatest(alive => {
    if (merkeDetalj) return // the detail view fetches its own data
    setLoading(true)
    setError(null)
    fetchStaatidMerker(filters, [], LIMIT, offset, { gruppering, q })
      .then(m => { if (alive()) setMerker(m.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [listKey, offset, merkeDetalj, gruppering])

  function openDetalj(merke: string, modell?: string | null) {
    // Merke & modell has always come back from a merke on page 1; Modeller
    // returns to the page the model was opened from (#695 AC6).
    if (!perModell) setPage({ key: listKey, offset: 0 })
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('merkeDetalj', merke)
      if (modell) next.set('modellDetalj', modell)
      else next.delete('modellDetalj')
      return next
    })
  }

  function closeDetalj() {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      // From a model page, "Tilbake" returns to the merke page it came from —
      // unless it was opened from the Modeller list, which it returns to.
      if (next.get('modellDetalj') && !perModell) next.delete('modellDetalj')
      else {
        next.delete('merkeDetalj')
        next.delete('modellDetalj')
      }
      return next
    })
  }

  // Rendered on the detail view too — the chips drive the detail chart there,
  // so hiding them on drill-down would make it unfilterable.
  const filterControls = (
    <FilterSlot>
      <StaatidFilterBar value={localDraft} onChange={setLocalDraft} />
    </FilterSlot>
  )

  if (merkeDetalj) {
    return (
      <>
        {filterControls}
        <MerkeDetalj
          // A fresh page per merke/modell, so a model opened from the Modeller
          // tab starts on Enkeltsalg over tid (#677 AC8).
          key={`${merkeDetalj}|${modellDetalj ?? ''}`}
          merke={merkeDetalj}
          modell={modellDetalj}
          baseFilters={filters}
          onBack={closeDetalj}
          onModellClick={m => openDetalj(merkeDetalj, m)}
        />
      </>
    )
  }

  if (error) {
    return (
      <>
        {filterControls}
        <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
          {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
        </div>
      </>
    )
  }

  return (
    <>
      {filterControls}
      <MerkerTable
        rows={merker?.rows ?? []}
        total={merker?.total ?? 0}
        onMerkeClick={m => openDetalj(m)}
        visModell={perModell}
        onModellClick={(m, mo) => openDetalj(m, mo)}
        offset={offset}
        limit={LIMIT}
        onPageChange={next => setPage({ key: listKey, offset: next })}
        // Skeleton only before the first answer: a refetch must keep the
        // search box mounted, or a search would lose focus mid-word (#476).
        loading={loading && !merker}
        exportMeta={{ dateRange: `${from} – ${to}` }}
        search={search}
        onSearchChange={setSearch}
      />
    </>
  )
}
